import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Save, Plus, Trash2, Info, AlertTriangle, BarChart3, Layers, ArrowRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import VraisemblanceMatrixModal from './VraisemblanceMatrixModal';
import { calculateVraisemblance as calcVraisemblance } from '../../utils/vraisemblanceCalculator';

/**
 * ScenarioEditModal - Modal for editing operational scenarios
 * Supports "Modes Operatoires" structure:
 * - Pool of Elementary Actions (Operational Steps)
 * - Modes Operatoires (Sequences of actions)
 */
const ScenarioEditModal = ({
  scenario,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  ctiData = null,  // CTI analysis data for vulnerabilities
  businessAssets = [],  // Business assets from attack path
  initialTab = 'general'
}) => {
  const { t } = useTranslation();

  // Debug: Check what data is being passed
  console.log('[ScenarioEditModal] businessAssets:', businessAssets);
  console.log('[ScenarioEditModal] ctiData:', ctiData);
  console.log('[ScenarioEditModal] ctiData?.vulnerabilities:', ctiData?.vulnerabilities);

  const [activeTab, setActiveTab] = useState(initialTab); // general, actions, modes

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const [editedScenario, setEditedScenario] = useState({
    name: '',
    description: '',
    severity: 'Moyen',
    timeline: '1-7 jours',
    requiredSkills: 'Intermédiaire',
    detectionDifficulty: 'Moyen',
    operationalSteps: [],
    modesOperatoires: []
  });
  const [showVraisemblanceMatrix, setShowVraisemblanceMatrix] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize edited scenario when modal opens
  useEffect(() => {
    if (isOpen && scenario) {
      let initialSteps = [];
      let initialModes = [];

      // Merge ALL step sources: manual scenarios store steps in elementaryActions,
      // AI-accepted steps (new_N) live in operationalSteps. Both must be visible.
      const elementary = (scenario.elementaryActions || []).length > 0
        ? scenario.elementaryActions
        : [];
      const operational = scenario.operationalSteps || scenario.steps || [];
      const seen = new Set();
      initialSteps = [...elementary, ...operational].filter((s) => {
        const key = String(s.elementaryActionId || s.id || s._id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      initialSteps = initialSteps.map((step, idx) => ({
        ...step,
        elementaryActionId: step.elementaryActionId || `action_${Date.now()}_${idx}`,
        name: step.name || step.technique || '',
        technique: step.technique || step.name || '',
        ps: step.ps || step.successProbability || step.stepProbability || 2,
        dt: step.dt || step.technicalDifficulty || step.difficulty || 2,
        vraisemblance: calcVraisemblance(step.ps || step.successProbability || 2, step.dt || step.technicalDifficulty || 2),
        duration: step.duration || '',
        tools: Array.isArray(step.tools) ? step.tools : (step.tools ? [step.tools] : []),
        indicators: Array.isArray(step.indicators) ? step.indicators : (step.indicators ? [step.indicators] : []),
        // Preserve fields if they exist
        dependencies: Array.isArray(step.dependencies) ? step.dependencies : [],
        nextActions: Array.isArray(step.nextActions) ? step.nextActions : [],
        assets: Array.isArray(step.assets) ? step.assets : [],
        vulnerabilities: Array.isArray(step.vulnerabilities) ? step.vulnerabilities : []
      }));

      if (scenario.modesOperatoires && scenario.modesOperatoires.length > 0) {
        initialModes = JSON.parse(JSON.stringify(scenario.modesOperatoires));
      } else {
        if (initialSteps.length > 0) {
          initialModes = [{
            id: `mode_${Date.now()}`,
            name: t('workshop4.operationalScenarios.editModal.modes.defaultModeName', 'Mode Opératoire Principal'),
            description: t('workshop4.operationalScenarios.editModal.modes.defaultModeDescription', 'Séquence standard'),
            path: initialSteps.map(s => s.elementaryActionId),
            vraisemblance: 2
          }];
        }
      }

      setEditedScenario({
        ...scenario,
        name: scenario.name || '',
        description: scenario.description || '',
        severity: scenario.severity || 'Moyen',
        timeline: scenario.timeline || '1-7 jours',
        requiredSkills: scenario.requiredSkills || 'Intermédiaire',
        detectionDifficulty: scenario.detectionDifficulty || 'Moyen',
        operationalSteps: initialSteps,
        modesOperatoires: initialModes
      });

      recalculateAll(initialSteps, initialModes);
    }
  }, [isOpen, scenario]);

  // Recalculate using EBIOS RM Advanced method
  // V(Mode) = MATRIX[MAX(DT)][MIN(PS)], V(Scenario) = MAX(V modes)
  const recalculateAll = (steps, modes) => {
    console.log('[recalculateAll] Steps:', steps.length, 'Modes:', modes.length);

    const updatedModes = modes.map(mode => {
      const path = mode.path || [];
      console.log('[recalculateAll] Mode:', mode.name, 'Path:', path);

      // Find actions that match the path - try multiple ID fields
      const modeActions = path.map(id => {
        return steps.find(s =>
          s.elementaryActionId === id ||
          s.id === id ||
          s._id === id
        );
      }).filter(Boolean);

      console.log('[recalculateAll] Mode:', mode.name, 'Found actions:', modeActions.length);

      if (modeActions.length === 0) {
        console.warn('[recalculateAll] No actions found for mode:', mode.name, 'Returning V=1');
        return { ...mode, vraisemblance: 1 };
      }

      // Extract PS and DT values
      const psValues = modeActions.map(a => {
        const ps = a.ps || a.successProbability || a.stepProbability || 2;
        console.log('[recalculateAll] Action:', a.name, 'PS:', ps, 'DT:', a.dt || a.technicalDifficulty || 2);
        return ps;
      }).filter(v => v >= 1 && v <= 4);

      const dtValues = modeActions.map(a => a.dt || a.technicalDifficulty || a.difficulty || 2).filter(v => v >= 1 && v <= 4);

      if (psValues.length === 0) return { ...mode, vraisemblance: 2 };

      const minPS = Math.min(...psValues);
      const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;
      const modeV = calcVraisemblance(minPS, maxDT);

      console.log('[recalculateAll] Mode:', mode.name, 'MIN(PS):', minPS, 'MAX(DT):', maxDT, '=> V:', modeV);

      return { ...mode, vraisemblance: modeV };
    });

    const retainedModeVs = updatedModes
      .filter((m) => m && m.retained !== false)
      .map((m) => m.vraisemblance)
      .filter((v) => v >= 1 && v <= 4);
    const scenarioV = retainedModeVs.length > 0
      ? Math.max(...retainedModeVs)
      : null;

    console.log('[recalculateAll] Scenario V:', scenarioV);

    setEditedScenario(prev => ({
      ...prev,
      operationalSteps: steps,
      modesOperatoires: updatedModes,
      scenarioVraisemblance: scenarioV
    }));
  };

  const addAction = () => {
    const newAction = {
      elementaryActionId: `action_${Date.now()}`,
      phase: 'CONNAITRE',
      name: '',
      description: '',
      ps: 2,
      dt: 2,
      vraisemblance: 2,
      duration: '',
      tools: [],
      indicators: [],
      // Fields for AI-like structure
      dependencies: [],
      nextActions: [],
      assets: [],
      vulnerabilities: []
    };
    const newSteps = [...editedScenario.operationalSteps, newAction];
    setEditedScenario(prev => ({ ...prev, operationalSteps: newSteps }));
  };

  const updateAction = (id, field, value) => {
    const newSteps = editedScenario.operationalSteps.map(step => {
      if (step.elementaryActionId === id) {
        const updatedStep = { ...step, [field]: value };
        if (field === 'ps' || field === 'dt') {
          updatedStep.vraisemblance = calcVraisemblance(updatedStep.ps || 2, updatedStep.dt || 2);
        }
        return updatedStep;
      }
      return step;
    });
    recalculateAll(newSteps, editedScenario.modesOperatoires);
  };

  const deleteAction = (id) => {
    const newSteps = editedScenario.operationalSteps.filter(s => s.elementaryActionId !== id);
    const newModes = editedScenario.modesOperatoires.map(mode => ({
      ...mode,
      path: mode.path.filter(actionId => actionId !== id)
    }));
    recalculateAll(newSteps, newModes);
  };

  const addMode = () => {
    const newMode = {
      id: `mode_${Date.now()}`,
      name: t('workshop4.operationalScenarios.editModal.modes.newMode', 'Nouveau Mode Opératoire'),
      description: '',
      path: [],
      vraisemblance: 1
    };
    const newModes = [...editedScenario.modesOperatoires, newMode];
    recalculateAll(editedScenario.operationalSteps, newModes);
  };

  const updateMode = (id, field, value) => {
    const newModes = editedScenario.modesOperatoires.map(mode =>
      mode.id === id ? { ...mode, [field]: value } : mode
    );
    recalculateAll(editedScenario.operationalSteps, newModes);
  };

  const toggleActionInMode = (modeId, actionId) => {
    const newModes = editedScenario.modesOperatoires.map(mode => {
      if (mode.id === modeId) {
        const path = [...mode.path];
        const idx = path.indexOf(actionId);
        if (idx >= 0) {
          path.splice(idx, 1);
        } else {
          path.push(actionId);
        }
        return { ...mode, path };
      }
      return mode;
    });
    recalculateAll(editedScenario.operationalSteps, newModes);
  };

  const deleteMode = (id) => {
    const newModes = editedScenario.modesOperatoires.filter(m => m.id !== id);
    recalculateAll(editedScenario.operationalSteps, newModes);
  };

  const handleSave = () => {
    const newErrors = {};
    if (!editedScenario.name?.trim()) newErrors.name = t('workshop4.operationalScenarios.editModal.errors.nameRequired', 'Le nom est requis');
    if (editedScenario.operationalSteps.length === 0) {
      newErrors.general = t('workshop4.operationalScenarios.editModal.errors.actionsRequired', 'Au moins une action élémentaire est requise');
    }
    if (editedScenario.modesOperatoires.length === 0) {
      newErrors.general = t('workshop4.operationalScenarios.editModal.errors.modesRequired', 'Au moins un mode opératoire est requis');
    }
    editedScenario.operationalSteps.forEach(step => {
      if (!step.name?.trim()) newErrors[`action_${step.elementaryActionId}`] = t('workshop4.operationalScenarios.editModal.errors.actionNameRequired', 'Nom requis');
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // AUTO-CALCULATE dependencies and nextActions from modesOperatoires
    // This creates the attack graph structure automatically
    const calculateDependenciesAndNextActions = () => {
      const dependenciesMap = new Map(); // actionId -> Set of dependencies
      const nextActionsMap = new Map();  // actionId -> Set of next actions

      // Initialize maps
      editedScenario.operationalSteps.forEach(step => {
        dependenciesMap.set(step.elementaryActionId, new Set());
        nextActionsMap.set(step.elementaryActionId, new Set());
      });

      // Analyze each mode to build the graph
      editedScenario.modesOperatoires.forEach(mode => {
        const path = mode.path || [];

        for (let i = 0; i < path.length; i++) {
          const currentActionId = path[i];

          // Add dependencies (all previous actions in this path)
          if (i > 0) {
            const previousActionId = path[i - 1];
            dependenciesMap.get(currentActionId)?.add(previousActionId);
          }

          // Add next actions (all following actions in this path)
          if (i < path.length - 1) {
            const nextActionId = path[i + 1];
            nextActionsMap.get(currentActionId)?.add(nextActionId);
          }
        }
      });

      // Convert Sets to Arrays
      return {
        dependenciesMap: new Map(
          Array.from(dependenciesMap.entries()).map(([id, set]) => [id, Array.from(set)])
        ),
        nextActionsMap: new Map(
          Array.from(nextActionsMap.entries()).map(([id, set]) => [id, Array.from(set)])
        )
      };
    };

    const { dependenciesMap, nextActionsMap } = calculateDependenciesAndNextActions();

    // First, normalize the elementary actions
    const normalizedElementaryActions = editedScenario.operationalSteps.map((step, idx) => ({
      ...step,
      technique: step.name || step.technique || 'Action sans nom',
      stepNumber: idx + 1,
      ps: step.ps || 2,
      dt: step.dt || 2,
      successProbability: step.ps || step.successProbability || 2,
      technicalDifficulty: step.dt || step.technicalDifficulty || 2,
      vraisemblance: step.vraisemblance || calcVraisemblance(step.ps || 2, step.dt || 2),
      tools: Array.isArray(step.tools) ? step.tools : (step.tools ? [step.tools] : []),
      indicators: Array.isArray(step.indicators) ? step.indicators : (step.indicators ? [step.indicators] : []),
      description: step.description || step.name || '',
      dependencies: dependenciesMap.get(step.elementaryActionId) || [],
      nextActions: nextActionsMap.get(step.elementaryActionId) || [],
      assets: Array.isArray(step.assets) ? step.assets : [],
      vulnerabilities: Array.isArray(step.vulnerabilities) ? step.vulnerabilities : []
    }));

    // Build final normalized scenario
    const normalizedScenario = {
      ...editedScenario,
      severity: editedScenario.severity || 'Moyen',
      timeline: editedScenario.timeline || '1-7 jours',
      requiredSkills: editedScenario.requiredSkills || 'Intermédiaire',
      detectionDifficulty: editedScenario.detectionDifficulty || 'Moyen',
      // operationalSteps deprecated - info consolidated in modesOperatoires
      operationalSteps: [],
      // elementaryActions contains the detailed actions
      elementaryActions: normalizedElementaryActions,
      modesOperatoires: editedScenario.modesOperatoires,
      scenarioVraisemblance: editedScenario.scenarioVraisemblance
    };

    console.log('[ScenarioEditModal] Saving normalized scenario:', normalizedScenario);

    onSave(normalizedScenario);
  };

  const getVraisemblanceColor = (v) => {
    if (v >= 4) return 'bg-red-100 text-red-800 border-red-200';
    if (v >= 3) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (v >= 2) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  if (!isOpen) return null;


  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">{t('workshop4.operationalScenarios.editModal.title', 'Modifier le Scénario')}</h2>
            <p className="text-blue-100 text-sm">{t('workshop4.operationalScenarios.editModal.subtitle', 'Édition des actions et modes opératoires')}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowVraisemblanceMatrix(true)}
              className="px-3 py-1.5 bg-white/20 text-white rounded hover:bg-white/30 transition text-sm flex items-center gap-2"
            >
              <BarChart3 size={16} /> {t('workshop4.operationalScenarios.editModal.matrix', 'Matrice')}
            </button>
            <button onClick={onClose} className="p-2 bg-white/20 text-white rounded hover:bg-white/30 transition">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 shrink-0">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'general' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {t('workshop4.operationalScenarios.editModal.tabs.general', 'Général')}
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'actions' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {t('workshop4.operationalScenarios.editModal.tabs.actions', 'Actions Élémentaires')} ({editedScenario.operationalSteps.length})
          </button>
          <button
            onClick={() => setActiveTab('modes')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'modes' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {t('workshop4.operationalScenarios.editModal.tabs.modes', 'Modes Opératoires')} ({editedScenario.modesOperatoires.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 min-h-0">
          {errors.general && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              {errors.general}
            </div>
          )}

          {activeTab === 'general' && (
            <div className="space-y-6 max-w-3xl mx-auto bg-white p-6 rounded-xl shadow-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.scenarioName', 'Nom du Scénario *')}</label>
                <input
                  type="text"
                  value={editedScenario.name}
                  onChange={(e) => setEditedScenario({ ...editedScenario, name: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder={t('workshop4.operationalScenarios.editModal.general.scenarioNamePlaceholder', 'Ex: Exfiltration de données via phishing')}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.description', 'Description')}</label>
                <textarea
                  value={editedScenario.description}
                  onChange={(e) => setEditedScenario({ ...editedScenario, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workshop4.operationalScenarios.editModal.general.descriptionPlaceholder', 'Description détaillée du scénario...')}
                />
              </div>

              {/* Risk Assessment Fields */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-4">{t('workshop4.operationalScenarios.editModal.general.riskAssessment', 'Évaluation du Risque')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.severity', 'Sévérité')}</label>
                    <select
                      value={editedScenario.severity || 'Moyen'}
                      onChange={(e) => setEditedScenario({ ...editedScenario, severity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="Très faible">{t('common.severity.veryLow', 'Très faible')}</option>
                      <option value="Faible">{t('common.severity.low', 'Faible')}</option>
                      <option value="Moyen">{t('common.severity.medium', 'Moyen')}</option>
                      <option value="Élevé">{t('common.severity.high', 'Élevé')}</option>
                      <option value="Très élevé">{t('common.severity.veryHigh', 'Très élevé')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.detectionDifficulty', 'Difficulté de détection')}</label>
                    <select
                      value={editedScenario.detectionDifficulty || 'Moyen'}
                      onChange={(e) => setEditedScenario({ ...editedScenario, detectionDifficulty: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="Très faible">Très faible</option>
                      <option value="Faible">Faible</option>
                      <option value="Moyen">Moyen</option>
                      <option value="Élevé">Élevé</option>
                      <option value="Très élevé">Très élevé</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.requiredSkills', 'Compétences requises')}</label>
                    <select
                      value={editedScenario.requiredSkills || 'Intermédiaire'}
                      onChange={(e) => setEditedScenario({ ...editedScenario, requiredSkills: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="Débutant">{t('common.skills.beginner', 'Débutant')}</option>
                      <option value="Intermédiaire">{t('common.skills.intermediate', 'Intermédiaire')}</option>
                      <option value="Avancé">{t('common.skills.advanced', 'Avancé')}</option>
                      <option value="Expert">{t('common.skills.expert', 'Expert')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshop4.operationalScenarios.editModal.general.timeline', 'Durée estimée (Timeline)')}</label>
                    <input
                      type="text"
                      value={editedScenario.timeline || ''}
                      onChange={(e) => setEditedScenario({ ...editedScenario, timeline: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder={t('workshop4.operationalScenarios.editModal.general.timelinePlaceholder', 'Ex: 1-7 jours, 2-4 semaines')}
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-900 mb-2">{t('workshop4.operationalScenarios.editModal.general.ebiomMetrics', 'Métriques EBIOS RM (Avancé)')}</h4>
                <p className="text-xs text-blue-700 mb-2">{t('workshop4.operationalScenarios.editModal.general.ebiomFormula', 'V(Mode) = MATRIX[MAX(DT)][MIN(PS)] → V(Scenario) = MAX des modes')}</p>
                <div className="flex gap-4">
                  <div className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${getVraisemblanceColor(editedScenario.scenarioVraisemblance)}`}>
                    {t('workshop4.operationalScenarios.editModal.general.likelihood', 'Vraisemblance')}: {editedScenario.scenarioVraisemblance}/4
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">{t('workshop4.operationalScenarios.editModal.actions.title', 'Actions Élémentaires')}</h3>
                <button
                  onClick={addAction}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition"
                >
                  <Plus size={18} /> {t('workshop4.operationalScenarios.editModal.actions.addAction', 'Ajouter une action')}
                </button>
              </div>

              <div className="grid gap-4">
                {editedScenario.operationalSteps.map((action, idx) => (
                  <div key={action.elementaryActionId} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-mono">#{idx + 1}</span>
                        <select
                          value={action.phase}
                          onChange={(e) => updateAction(action.elementaryActionId, 'phase', e.target.value)}
                          className="text-xs font-bold px-2 py-1 rounded border border-gray-300 bg-gray-50"
                        >
                          {['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={() => deleteAction(action.elementaryActionId)} className="text-red-400 hover:text-red-600 transition">
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <input
                          type="text"
                          value={action.name}
                          onChange={(e) => updateAction(action.elementaryActionId, 'name', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[`action_${action.elementaryActionId}`] ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder={t('workshop4.operationalScenarios.editModal.actions.actionName', 'Nom de l\'action')}
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 block mb-1">{t('workshop4.operationalScenarios.editModal.actions.ps', 'PS (Probabilité)')}</label>
                          <select
                            value={action.ps || 2}
                            onChange={(e) => updateAction(action.elementaryActionId, 'ps', parseInt(e.target.value))}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            {[1, 2, 3, 4].map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 block mb-1">{t('workshop4.operationalScenarios.editModal.actions.dt', 'DT (Difficulté)')}</label>
                          <select
                            value={action.dt || 2}
                            onChange={(e) => updateAction(action.elementaryActionId, 'dt', parseInt(e.target.value))}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            {[1, 2, 3, 4].map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 block mb-1">{t('workshop4.operationalScenarios.editModal.actions.vCalculated', 'V (Calculé)')}</label>
                          <div className={`w-full px-2 py-2 rounded-lg text-sm font-bold text-center border ${getVraisemblanceColor(action.vraisemblance)}`}>
                            {action.vraisemblance}/4
                          </div>
                        </div>
                      </div>
                    </div>

                    <textarea
                      value={action.description}
                      onChange={(e) => updateAction(action.elementaryActionId, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Description..."
                    />

                    <div className="grid md:grid-cols-2 gap-4 mt-3">
                      <div>
                        <label className="text-xs text-slate-600 font-medium block mb-1">{t('workshop4.operationalScenarios.editModal.actions.duration', 'Durée estimée')}</label>
                        <input
                          type="text"
                          value={action.duration || ''}
                          onChange={(e) => updateAction(action.elementaryActionId, 'duration', e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                          placeholder={t('workshop4.operationalScenarios.editModal.actions.durationPlaceholder', 'Ex: 30 minutes...')}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 font-medium block mb-1">{t('workshop4.operationalScenarios.editModal.actions.tools', 'Outils')}</label>
                        <input
                          type="text"
                          defaultValue={Array.isArray(action.tools) ? action.tools.join(', ') : (action.tools || '')}
                          onBlur={(e) => {
                            const toolsArray = e.target.value.split(',').map(t => t.trim()).filter(t => t.length > 0);
                            updateAction(action.elementaryActionId, 'tools', toolsArray);
                          }}
                          className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                          placeholder={t('workshop4.operationalScenarios.editModal.actions.toolsPlaceholder', 'Ex: Nmap, Metasploit...')}
                        />
                      </div>
                    </div>

                    {/* Indicators field */}
                    <div className="mt-2">
                      <label className="text-xs text-slate-600 font-medium block mb-1">{t('workshop4.operationalScenarios.editModal.actions.indicators', 'Indicateurs IOC')}</label>
                      <input
                        type="text"
                        defaultValue={Array.isArray(action.indicators) ? action.indicators.join(', ') : (action.indicators || '')}
                        onBlur={(e) => {
                          const indicatorsArray = e.target.value.split(',').map(i => i.trim()).filter(i => i.length > 0);
                          updateAction(action.elementaryActionId, 'indicators', indicatorsArray);
                        }}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                        placeholder={t('workshop4.operationalScenarios.editModal.actions.indicatorsPlaceholder', 'Ex: Connexions suspectes...')}
                      />
                    </div>

                    {/* Advanced Modeling - Compact Section */}
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                          <Layers size={12} /> Cibles & Exploitation
                        </span>
                        <span className="text-xs text-slate-500 italic">Dépendances auto-calculées</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {/* Assets - Compact MultiSelect + Input */}
                        <div>
                          <label className="text-xs text-slate-600 font-medium block mb-1">Actifs ciblés</label>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {(action.assets || []).map((asset, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {asset}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newAssets = (action.assets || []).filter((_, idx) => idx !== i);
                                    updateAction(action.elementaryActionId, 'assets', newAssets);
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                >×</button>
                              </span>
                            ))}
                          </div>
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value && !(action.assets || []).includes(e.target.value)) {
                                updateAction(action.elementaryActionId, 'assets', [...(action.assets || []), e.target.value]);
                              }
                              e.target.value = '';
                            }}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
                          >
                            <option value="">+ Ajouter un actif...</option>
                            {businessAssets.filter(a => !(action.assets || []).includes(a.name || a)).map((asset, i) => (
                              <option key={i} value={asset.name || asset}>{asset.name || asset}</option>
                            ))}
                            <option disabled>───────────</option>
                            <option value="__custom__">Saisir manuellement...</option>
                          </select>
                          {/* Custom input if needed */}
                          <input
                            type="text"
                            placeholder="Actif personnalisé + Entrée"
                            className="w-full mt-1 px-2 py-1 border border-slate-200 rounded text-xs"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const val = e.target.value.trim();
                                if (!(action.assets || []).includes(val)) {
                                  updateAction(action.elementaryActionId, 'assets', [...(action.assets || []), val]);
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>

                        {/* Vulnerabilities - Compact MultiSelect + Input */}
                        <div>
                          <label className="text-xs text-slate-600 font-medium block mb-1">Vulnérabilités</label>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {(action.vulnerabilities || []).map((vuln, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                                {vuln}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newVulns = (action.vulnerabilities || []).filter((_, idx) => idx !== i);
                                    updateAction(action.elementaryActionId, 'vulnerabilities', newVulns);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >×</button>
                              </span>
                            ))}
                          </div>
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value && !(action.vulnerabilities || []).includes(e.target.value)) {
                                updateAction(action.elementaryActionId, 'vulnerabilities', [...(action.vulnerabilities || []), e.target.value]);
                              }
                              e.target.value = '';
                            }}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
                          >
                            <option value="">+ Ajouter CVE...</option>
                            {(() => {
                              const ctiVulns = [];
                              if (ctiData?.vulnerabilities) {
                                ctiData.vulnerabilities.forEach(v => {
                                  if (v.id && !(action.vulnerabilities || []).includes(v.id)) ctiVulns.push(v.id);
                                  if (v.cveId && v.cveId !== v.id && !(action.vulnerabilities || []).includes(v.cveId)) ctiVulns.push(v.cveId);
                                });
                              }
                              return ctiVulns.sort().map(vuln => (
                                <option key={vuln} value={vuln}>{vuln}</option>
                              ));
                            })()}
                          </select>
                          <input
                            type="text"
                            placeholder="CVE-2025-XXXX + Entrée"
                            className="w-full mt-1 px-2 py-1 border border-slate-200 rounded text-xs"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const val = e.target.value.trim();
                                if (!(action.vulnerabilities || []).includes(val)) {
                                  updateAction(action.elementaryActionId, 'vulnerabilities', [...(action.vulnerabilities || []), val]);
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {editedScenario.operationalSteps.length === 0 && (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-dashed border-gray-300">
                    {t('workshop4.operationalScenarios.editModal.actions.noActions', 'Aucune action définie. Commencez par en ajouter une.')}
                  </div>
                )}
              </div>
            </div>
          )
          }

          {
            activeTab === 'modes' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">{t('workshop4.operationalScenarios.editModal.modes.title', 'Modes Opératoires')}</h3>
                  <button
                    onClick={addMode}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition"
                  >
                    <Plus size={18} /> {t('workshop4.operationalScenarios.editModal.modes.addMode', 'Ajouter un mode')}
                  </button>
                </div>

                <div className="grid gap-6">
                  {editedScenario.modesOperatoires.map((mode, idx) => (
                    <div key={mode.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="flex justify-between items-start mb-4 border-b border-gray-100 pb-4">
                        <div className="flex-1 mr-4">
                          <input
                            type="text"
                            value={mode.name}
                            onChange={(e) => updateMode(mode.id, 'name', e.target.value)}
                            className="text-lg font-bold text-gray-900 border-none focus:ring-0 p-0 w-full placeholder-gray-400"
                            placeholder={t('workshop4.operationalScenarios.editModal.modes.modeName', 'Nom du mode opératoire')}
                          />
                          <input
                            type="text"
                            value={mode.description}
                            onChange={(e) => updateMode(mode.id, 'description', e.target.value)}
                            className="text-sm text-gray-500 border-none focus:ring-0 p-0 w-full mt-1 placeholder-gray-300"
                            placeholder={t('workshop4.operationalScenarios.editModal.modes.modeDescription', 'Description courte...')}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-1 rounded-full text-xs font-bold border ${getVraisemblanceColor(mode.vraisemblance)}`}>
                            V: {mode.vraisemblance}/4
                          </div>
                          <button onClick={() => deleteMode(mode.id)} className="text-red-400 hover:text-red-600 transition" title={t('workshop4.operationalScenarios.editModal.modes.deleteMode', 'Supprimer ce mode')}>
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('workshop4.operationalScenarios.editModal.modes.actionSequence', 'Séquence d\'actions')}</h4>
                        <div className="space-y-2">
                          {editedScenario.operationalSteps.length > 0 ? (
                            <div className="grid grid-cols-1 gap-2">
                              {editedScenario.operationalSteps.map((action) => {
                                const isSelected = mode.path.includes(action.elementaryActionId);
                                const orderIndex = mode.path.indexOf(action.elementaryActionId);
                                return (
                                  <div
                                    key={action.elementaryActionId}
                                    onClick={() => toggleActionInMode(mode.id, action.elementaryActionId)}
                                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                                  >
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}`}>
                                      {isSelected && <Check size={12} className="text-white" />}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${action.phase === 'CONNAITRE' ? 'bg-blue-100 text-blue-800' : action.phase === 'RENTRER' ? 'bg-red-100 text-red-800' : action.phase === 'TROUVER' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                          {action.phase.substring(0, 3)}
                                        </span>
                                        <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                          {action.name || t('workshop4.operationalScenarios.editModal.actions.noName', 'Action sans nom')}
                                        </span>
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <div className="text-xs font-mono font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                        {t('workshop4.operationalScenarios.editModal.modes.step', 'Étape')} {orderIndex + 1}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 italic">
                              {t('workshop4.operationalScenarios.editModal.modes.noActionsAvailable', 'Aucune action disponible. Créez d\'abord des actions dans l\'onglet "Actions Élémentaires".')}
                            </div>
                          )}

                          {mode.path.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="text-xs text-gray-500 mb-2">{t('workshop4.operationalScenarios.editModal.modes.currentOrder', 'Ordre actuel:')}</div>
                              <div className="flex flex-wrap items-center gap-2">
                                {mode.path.map((actionId, i) => {
                                  const action = editedScenario.operationalSteps.find(a => a.elementaryActionId === actionId);
                                  return (
                                    <React.Fragment key={actionId}>
                                      {i > 0 && <ArrowRight size={14} className="text-gray-300" />}
                                      <span className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 text-gray-700" title={action?.name}>
                                        {action?.name?.substring(0, 15)}{action?.name?.length > 15 ? '...' : ''}
                                      </span>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {editedScenario.modesOperatoires.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-dashed border-gray-300">
                      {t('workshop4.operationalScenarios.editModal.modes.noModes', 'Aucun mode opératoire. Ajoutez-en un pour définir une séquence d\'attaque.')}
                    </div>
                  )}
                </div>
              </div>
            )
          }
        </div >

        {/* Footer - Always visible */}
        < div className="p-4 bg-white border-t-2 border-gray-300 flex justify-end gap-3 shrink-0" >
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
            {t('workshop4.operationalScenarios.editModal.footer.cancel', 'Annuler')}
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={18} />
            {isLoading ? t('workshop4.operationalScenarios.editModal.footer.saving', 'Sauvegarde...') : t('workshop4.operationalScenarios.editModal.footer.save', 'Sauvegarder')}
          </button>
        </div >
      </div >

      <VraisemblanceMatrixModal
        isOpen={showVraisemblanceMatrix}
        onClose={() => setShowVraisemblanceMatrix(false)}
      />
    </div >,
    document.body
  );
};

export default ScenarioEditModal;